'use client';

import { useTranslations } from 'next-intl';
import type { SurveyQuestion as QuestionConfig } from './survey-config';

interface SurveyQuestionProps {
  question: QuestionConfig;
  value: string | string[] | number | undefined;
  conditionalValue?: string;
  error?: string;
  sliderInteracted?: boolean;
  onChange: (questionId: string, value: string | string[] | number) => void;
  onConditionalChange: (questionId: string, value: string) => void;
  onSliderInteract: (questionId: string) => void;
}

export default function SurveyQuestion({
  question,
  value,
  conditionalValue,
  error,
  sliderInteracted,
  onChange,
  onConditionalChange,
  onSliderInteract,
}: SurveyQuestionProps) {
  const tq = useTranslations('landing.survey.questions');

  const label = tq(`${question.id}.label`);
  const hint = question.hasHint ? tq(`${question.id}.hint`) : undefined;

  return (
    <fieldset className="space-y-3" id={`question-${question.id}`}>
      <legend className="text-base font-semibold text-dark">
        <span className="text-teal-500 mr-2">Q{question.number}.</span>
        {label}
        {question.required && <span className="text-red-400 ml-1">*</span>}
      </legend>

      {hint && (
        <p className="text-sm text-gray-500">{hint}</p>
      )}

      {question.type === 'radio' && renderRadio(question, value as string, conditionalValue, onChange, onConditionalChange, tq)}
      {question.type === 'checkbox' && renderCheckbox(question, value as string[] | undefined, onChange, tq)}
      {question.type === 'slider' && renderSlider(question, value as number | undefined, sliderInteracted, onChange, onSliderInteract, tq)}
      {question.type === 'textarea' && renderTextarea(question, value as string | undefined, onChange, tq)}

      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </fieldset>
  );
}

function renderRadio(
  question: QuestionConfig,
  value: string | undefined,
  conditionalValue: string | undefined,
  onChange: (id: string, val: string) => void,
  onConditionalChange: (id: string, val: string) => void,
  tq: ReturnType<typeof useTranslations>,
) {
  return (
    <div className="space-y-2">
      {question.options?.map((option) => (
        <div key={option.value}>
          <label
            className={`flex items-center gap-3 p-3 rounded-brand border cursor-pointer transition-colors ${
              value === option.value
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-200 bg-surface hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name={question.id}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(question.id, option.value)}
              className="sr-only"
            />
            <span
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                value === option.value ? 'border-teal-500' : 'border-gray-300'
              }`}
            >
              {value === option.value && (
                <span className="w-2 h-2 rounded-full bg-teal-500" />
              )}
            </span>
            <span className="text-sm text-dark">{tq(`${question.id}.options.${option.value}`)}</span>
          </label>

          {option.triggersInput && value === option.value && (
            <input
              type="text"
              value={conditionalValue || ''}
              onChange={(e) => onConditionalChange(question.id, e.target.value)}
              placeholder={tq(`${question.id}.inputPlaceholder`)}
              className="mt-2 ml-7 w-[calc(100%-1.75rem)] px-3 py-2 text-sm border border-gray-200 rounded-brand focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              maxLength={500}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function renderCheckbox(
  question: QuestionConfig,
  values: string[] | undefined,
  onChange: (id: string, val: string[]) => void,
  tq: ReturnType<typeof useTranslations>,
) {
  const selected = values || [];
  const maxReached = question.maxSelections ? selected.length >= question.maxSelections : false;

  return (
    <div className="space-y-2">
      {question.options?.map((option) => {
        const isChecked = selected.includes(option.value);
        const isDisabled = !isChecked && maxReached;

        return (
          <label
            key={option.value}
            className={`flex items-center gap-3 p-3 rounded-brand border cursor-pointer transition-colors ${
              isChecked
                ? 'border-teal-500 bg-teal-50'
                : isDisabled
                  ? 'border-gray-100 bg-surface-alt opacity-50 cursor-not-allowed'
                  : 'border-gray-200 bg-surface hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              value={option.value}
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                const next = isChecked
                  ? selected.filter((v) => v !== option.value)
                  : [...selected, option.value];
                onChange(question.id, next);
              }}
              className="sr-only"
            />
            <span
              className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                isChecked ? 'border-teal-500 bg-teal-500' : 'border-gray-300'
              }`}
            >
              {isChecked && (
                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="text-sm text-dark">{tq(`${question.id}.options.${option.value}`)}</span>
          </label>
        );
      })}
    </div>
  );
}

function renderSlider(
  question: QuestionConfig,
  value: number | undefined,
  interacted: boolean | undefined,
  onChange: (id: string, val: number) => void,
  onInteract: (id: string) => void,
  tq: ReturnType<typeof useTranslations>,
) {
  const min = question.sliderMin ?? 1;
  const max = question.sliderMax ?? 5;
  const displayValue = value ?? Math.ceil((min + max) / 2);

  const leftLabel = tq(`${question.id}.sliderLabels.${min}`);
  const rightLabel = tq(`${question.id}.sliderLabels.${max}`);
  const currentLabel = tq(`${question.id}.sliderLabels.${displayValue}`);

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-400 w-16 text-right">{leftLabel}</span>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={displayValue}
          onChange={(e) => {
            if (!interacted) onInteract(question.id);
            onChange(question.id, parseInt(e.target.value));
          }}
          className="flex-1 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-teal-500"
        />
        <span className="text-xs text-gray-400 w-20">{rightLabel}</span>
      </div>

      {interacted && currentLabel && (
        <p className="text-center text-sm font-medium text-teal-600">
          {displayValue} — {currentLabel}
        </p>
      )}
    </div>
  );
}

function renderTextarea(
  question: QuestionConfig,
  value: string | undefined,
  onChange: (id: string, val: string) => void,
  tq: ReturnType<typeof useTranslations>,
) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(question.id, e.target.value)}
      placeholder={tq(`${question.id}.placeholder`)}
      maxLength={2000}
      rows={3}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-brand focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-y"
    />
  );
}
